// Footer.js — Revised for the donation framing
// Changes vs previous:
//  - Added a "Mission" column linking to About + the 6 NGOs
//  - Collapsed the redundant "Connect" column into Resources (was just email + contact form)
//  - Kept language switcher
//  - Tagline updated to reflect the actual project framing

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GAEvents } from '../utils/analytics';
import '../styles/Footer.css';

const Footer = () => {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    const fromLang = i18n.language;
    i18n.changeLanguage(lng);
    GAEvents.languageChanged(fromLang, lng);
  };

  // Same canonical list of organizations as donation.py and AboutPage.jsx
  const organizations = [
    { name: 'UNRWA', url: 'https://www.unrwa.org/donate' },
    { name: 'MAP', url: 'https://www.map.org.uk/donate' },
    { name: 'MSF', url: 'https://www.msf.org/donate' },
    { name: 'Islamic Relief', url: 'https://www.islamic-relief.org/gaza-emergency/' },
    { name: 'Lebanese Red Cross', url: 'https://www.redcross.org.lb/donate' },
    { name: 'UNHCR Lebanon', url: 'https://www.unhcr.org/lb/donate' },
  ];

  return (
    <footer className="footer">
      <div className="footer-content">

        {/* COLUMN 1: Product */}
        <div className="footer-section">
          <h4>{t('footer.product') || 'Product'}</h4>
          <ul>
            <li><Link to="/">{t('nav.home') || 'Home'}</Link></li>
            <li><Link to="/dashboard">{t('nav.dashboard') || 'Dashboard'}</Link></li>
            <li><Link to="/signup">{t('nav.signup') || 'Join Beta'}</Link></li>
            <li><Link to="/login">{t('nav.login') || 'Login'}</Link></li>
          </ul>
        </div>

        {/* COLUMN 2: Mission — new */}
        <div className="footer-section">
          <h4>{t('footer.mission') || 'Mission'}</h4>
          <ul>
            <li><Link to="/about">{t('footer.about') || 'About this project'}</Link></li>
            <li><Link to="/about#orgs">{t('footer.organizations') || 'Organizations'}</Link></li>
            <li><Link to="/about#how">{t('footer.how_donations_work') || 'How donations work'}</Link></li>
          </ul>
        </div>

        {/* COLUMN 3: Organizations (direct external links) */}
        <div className="footer-section">
          <h4>{t('footer.donate_directly') || 'Donate directly'}</h4>
          <ul>
            {organizations.map((org) => (
              <li key={org.name}>
                <a href={org.url} target="_blank" rel="noopener noreferrer">{org.name}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* COLUMN 4: Resources (collapsed Connect into here) */}
        <div className="footer-section">
          <h4>{t('footer.resources') || 'Resources'}</h4>
          <ul>
            <li><Link to="/contact">{t('footer.contact') || 'Contact'}</Link></li>
            <li>
              <a href="mailto:betterpdp@gmail.com">{t('footer.email') || 'Email'}</a>
            </li>
            <li><Link to="/privacy">{t('footer.privacy') || 'Privacy'}</Link></li>
            <li><Link to="/terms">{t('footer.terms') || 'Terms'}</Link></li>
            <li><Link to="/cookies">{t('footer.cookies') || 'Cookies'}</Link></li>
          </ul>
        </div>

        {/* COLUMN 5: Language */}
        <div className="footer-section">
          <h4>{t('footer.language') || 'Language'}</h4>
          <div className="language-switcher">
            <button
              className={`lang-btn ${i18n.language === 'it' ? 'active' : ''}`}
              onClick={() => changeLanguage('it')}
              aria-label="Italiano"
            >
              🇮🇹 Italiano
            </button>
            <button
              className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
              onClick={() => changeLanguage('en')}
              aria-label="English"
            >
              🇬🇧 English
            </button>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-brand">
          <h3>Better<span className="highlight">PDP</span></h3>
          <p className="footer-tagline">
            {t('footer.description_new') || 'Free during beta. Donations support humanitarian aid in Gaza and Lebanon.'}
          </p>
        </div>

        <p className="footer-copyright">
          {t('footer.copyright') || `© ${new Date().getFullYear()} BetterPDP. A personal project by Federico.`}
        </p>
      </div>
    </footer>
  );
};

export default Footer;